import { ApiResponse } from 'modules/auth/interfaces/apiResponse';
import { AuthApi } from './../api/auth';
import axios, { AxiosInstance } from 'axios';
import environment from '@/environment';
import store from '@/store';
import router from '@/router';
import { Message } from 'element-ui';
import i18n from '@/i18n/index';

type CallbackFunction = (data?: any) => Promise<any>;
interface AxiosAuthorizedApi extends AxiosInstance {
  simpleGet: (
    apiEndpoint: string,
    callbackSuccess: CallbackFunction,
    callbackError: CallbackFunction,
  ) => void;
  simplePost: (
    apiEndpoint: string,
    postData: any,
    callbackSuccess: CallbackFunction,
    callbackError: CallbackFunction,
  ) => void;
}

const AuthorizedApiClient: AxiosAuthorizedApi = Object.assign(
  axios.create({
    baseURL: environment.backendUrl + environment.apiPrefix,
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  {
    simplePost(
      apiEndpoint: string,
      postData: any,
      callbackSuccess: CallbackFunction,
      callbackError: CallbackFunction,
      ) {
      AuthorizedApiClient.post(apiEndpoint, postData)
        .then((response: any) => {
          if (response.status === 200) {
            const responseData = response.data;

            if (responseData.success) {
              callbackSuccess(responseData.data);
            } else {
              callbackError(responseData.message);
            }
          } else {
            callbackError();
          }
        })
        .catch((error: Error) => {
          callbackError(error);
        });
    },

    simpleGet(
      apiEndpoint: string,
      callbackSuccess: CallbackFunction,
      callbackError: CallbackFunction,
    ) {
      AuthorizedApiClient.get(apiEndpoint)
        .then((response: any) => {
          if (response.status === 200) {
            const responseData = response.data;

            if (responseData.success) {
              callbackSuccess(responseData.data);
            } else {
              callbackError(responseData.message);
            }
          } else {
            callbackError();
          }
        })
        .catch((error: Error) => {
          callbackError(error);
        });
    },
  },
);

AuthorizedApiClient.interceptors.request.use(
  (config: any) => {
    if (store.state.auth && store.state.auth.accessToken) {
      config.headers = Object.assign(config.headers, {
        Authorization: 'Bearer ' + store.state.auth.accessToken,
      });
    }

    return config;
  },
  (error: Error) => {
    return Promise.reject(error);
  },
);

AuthorizedApiClient.interceptors.response.use(function (response) {
  return response;
}, function (error) {
  if (error.response && error.response.status === 403) {
    const personalAssistantId = store.state.auth?.user?.personalAssistant?.id;

    if (!personalAssistantId) {
      router.push('/dashboard');
    } else {
      router.push('/select-broker');
    }
  }
  
  // Always re-throw the error so the next interceptor can handle it
  throw error;
})

AuthorizedApiClient.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (error: any) => {
    console.error('Request error'); // tslint:disable-line
    console.error(error.response); // tslint:disable-line

    if (error.response && error.response.status === 401) {
      if (error.config.url.includes('/account/login/refresh/')) {
        store.commit('auth/logout');
        router.push('/login');
        throw error;
      } else {
        const api = new AuthApi();
        api.refreshToken().then((response: ApiResponse<any>) => {
          return AuthorizedApiClient({
            method: error.config.method,
            url: error.config.url,
            data: error.config.data,
            headers: error.config.headers,
          });
        }).catch((catchError: any) => {
          throw catchError;
        });
      }
    }

    // Re-throw the error so individual API methods can handle it
    throw error;
  },
);

export default AuthorizedApiClient;
